import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApplicationElement } from './application_element.js';

import { downloadContent, shareContent, dateToInput, dateFormat } from '../lib/utils.js';

import  { VERSION } from '../version.js';

import { styles as sharedStyles } from '../styles/shared-styles'

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';

import { Session } from '../models/session.js';

@customElement('app-settings')
export class AppSettings extends ApplicationElement {
  static styles = [
    sharedStyles,
    css`

    `
  ]

  @property({ type: Object }) appConfig: any = {
    version: VERSION
  };

  @state() settings: any = this.state?.settings

  updateSettings(event) {
    const key = event.target.dataset.key;
    const value = event.target.checked;

    this.settings.update({ [key]: value })
  }

  onStateChange({ state }) {
    this.settings = state.settings;
  }

  async exportData() {
    const dump = await Session.dumpToJson();
    downloadContent(dump, `fasting_flux_${Date.now()}.json`, 'application/json');
    this.state.config.update({ lastBackupAt: new Date() });
  }

  async shareData() {
    const dump = await Session.dumpToJson();
    const success = await shareContent(dump, `fasting_flux_${Date.now()}.json.txt`, 'text/plain');
    if(success) this.state.config.update({ lastBackupAt: new Date() });
  }

  async openFilePicker() {
    const [fileHandle] = await window.showOpenFilePicker();
    return await fileHandle.getFile();
  }

  async importData() {
    const file = await this.openFilePicker();
    const content = await file.text();

    const sessions = JSON.parse(content);

    if (!sessions) {
      alert('Invalid data');
      throw new Error('Invalid data');
    }

    await Session.truncate();
    await Session.upsertAllInBatches(sessions);

    alert('Sessions imported successfully');
  }



  renderSetting({key, label, description}) {
    const checked = this.settings?.[key];

    return html`
      <li>
        <sl-switch data-key=${key} ?checked=${checked} @sl-input=${this.updateSettings}>
          ${label}
          ${description && html`<span class="desc">${description}</span>`}
        </sl-switch>
      </li>
    `;
  }

  updatePlan(event) {
    const {value} = event.target;

    const plan = value.split('/').map(v => parseInt(v, 10));

    this.settings.update({
      fastingGoal: plan[0],
      eatingGoal: plan[1]
    })
  }

  fastingPlans = [
    { value: '12/12', label: '(12/12) Easy - 12 hours fasting, 12 hours eating' },
    { value: '14/10', label: '(14/10) Easy - 14 hours fasting, 10 hours eating' },
    { value: '16/8', label: '(16/8) Intermediate - 16 hours fasting, 8 hours eating' },
    { value: '18/6', label: '(18/6) Intermediate - 18 hours fasting, 6 hours eating' },
    { value: '20/4', label: '(20/4) Hard - 20 hours fasting, 4 hours eating' },
    { value: '22/2', label: '(22/2) Hard - 22 hours fasting, 2 hours eating' },
  ]

  get currentPlan() {
    return `${this.settings.fastingGoal}/${this.settings.eatingGoal}`;
  }

  updateStatsBeginsAt(event) {
    const { name, valueAsDate } = event.target;

    this.settings.update({ [name]: valueAsDate });
  }

  render() {
    return html`
      <app-header enableBack title="Settings"></app-header>
      <main>

        <h2 class="section-title">All</h2>
        <section class="card">

          <sl-select value=${this.currentPlan} @sl-change=${this.updatePlan} label="Choose your fasting plan" name="fastingPlan">
            ${this.fastingPlans.map(({ label, value }) => html`<sl-option value=${value}>${label}</sl-option>`) }
          </sl-select>

          <br>
          <sl-input
            label="Calculate stats from"
            type="date"
            name="statsBeginsAt"
            .valueAsDate=${this.settings?.statsBeginsAt}
            help-text="If empty, stats will be calculated from the first session"
            @sl-input=${this.updateStatsBeginsAt}></sl-input>

        </section>


        <h2 class="section-title">Export</h2>
        <section class="card">
          <p>This will download sessions as JSON</p>
          <p>Last backup at: ${this.state?.config?.lastBackupAt ? dateFormat(this.state.config.lastBackupAt) : 'Never'}</p>
          <sl-button variant="primary" @click=${this.exportData}>Download</sl-button>
          <sl-button variant="primary" @click=${this.shareData}>Share</sl-button>
        </section>

        <h2 class="section-title">Import</h2>
        <section class="card">
          <p>This will delete all current sessions and import new ones from a JSON file using upsert method.</p>
          <sl-button variant="primary" @click=${this.importData}>Import sessions</sl-button>
        </section>

      </main>
    `;
  }
}
